import { createPortal } from 'react-dom';
import { prop, pick } from 'ramda';
import { directRequire } from 'js/backbone/portlets';
import { getCleanPath } from 'js/utils/url.util';
import { usePrevious } from 'js/hooks/usePrevious';
import React, { useRef, useEffect, useState } from 'react';
import { WebContent } from 'js/components/system/WebContent';
import { componentService } from 'js/services/component.service';
import { useDeviceSelector, useAppSelector } from 'js/store/hooks';
import { SLIDE_MENU_CLASS_NAME } from 'js/components/main/SlideMenu';
import { BackboneWrapper } from 'js/backbone/components/BackboneWrapper';

import * as hooks from './hooks';
import style from './DefaultMenu.scss';

const DefaultMenuWrapper = ({ slideMenuWcId = null }) => {
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const elRef = useRef();
    const [menuView, setMenuView] = useState(null);
    const backgroundPage = hooks.useGetBackgroundPage();
    const menuItems = useAppSelector(prop('menuItems'));
    const prevSlideMenuWcId = usePrevious(slideMenuWcId);
    const { sportsbookRootURL, lottoRootURL } = useAppSelector(pick(['sportsbookRootURL', 'lottoRootURL']));
    const sportPageRegExp = new RegExp(`(^${sportsbookRootURL}$)|(^${sportsbookRootURL}/)`);
    const lottoPageRegExp = new RegExp(`(^${lottoRootURL}$)|(^${lottoRootURL}/)`);
    const isBackgroundSportPage = sportPageRegExp.test(getCleanPath(backgroundPage));
    const isBackgroundLottoPage = lottoPageRegExp.test(getCleanPath(backgroundPage));
    const sliderContainer = document.querySelector(`.${SLIDE_MENU_CLASS_NAME}`);

    useEffect(() => {
        if (!sliderContainer && !isDesktop || !menuItems || !menuItems.length || menuView) {
            return;
        }

        const [MenuView] = directRequire(['modules_menu_menu_view']);
        const view = new MenuView({ el: elRef.current });

        setMenuView(view);
        view.setSlideMenuWcId(slideMenuWcId);
        view.render();
    }, [setMenuView, menuItems, menuView, slideMenuWcId, isDesktop, sliderContainer]);

    useEffect(() => () => menuView?.closeView(), [menuView]);

    useEffect(() => {
        if (isDesktop || !menuView || slideMenuWcId === prevSlideMenuWcId) {
            return;
        }

        menuView.setSlideMenuWcId(slideMenuWcId);
        menuView.render();
    }, [isDesktop, menuView, slideMenuWcId, prevSlideMenuWcId]);

    useEffect(() => {
        if (!menuView) {
            return;
        }

        if (sportsbookRootURL && isBackgroundSportPage || lottoRootURL && isBackgroundLottoPage) {
            menuView.activateMenu();
            menuView.setBackgroundPage();
        } else {
            menuView.activateMenu(backgroundPage);
            menuView.setBackgroundPage(backgroundPage);
        }
    }, [
        backgroundPage,
        isBackgroundLottoPage,
        isBackgroundSportPage,
        lottoRootURL,
        menuView,
        sportsbookRootURL
    ]);

    if (isDesktop) {
        return <BackboneWrapper el={elRef} />;
    }

    return sliderContainer
        ? createPortal(
            (
                <BackboneWrapper el={elRef}>
                    {slideMenuWcId && (
                        <div className={`${style.customSlideMenuWrapper} cms-default-menu-custom-slider`}>
                            <div className={style.customSlideMenu}>
                                <WebContent id={slideMenuWcId} />
                            </div>
                        </div>
                    )}
                </BackboneWrapper>
        ), sliderContainer)
        : null;
};

export const DefaultMenu = ({ slideMenuWcId }) => <DefaultMenuWrapper slideMenuWcId={slideMenuWcId} />;

componentService.registerWebContentComponents({ DefaultMenu });
